import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { UserListRow } from '../../services/user-service/user.service';

@Component({
  selector: 'app-user-roles-cell-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    @if (roles.length > 0) {
      <div class="user-role-chips">
        @for (role of roles; track role) {
          <span class="user-role-chip">{{ role }}</span>
        }
      </div>
    } @else {
      <span class="user-role-empty">N/A</span>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
        max-width: 200px;
        overflow: hidden;
        box-sizing: border-box;
      }

      .user-role-chips {
        display: flex;
        flex-wrap: wrap;
        align-content: flex-start;
        gap: 4px;
        width: 100%;
        max-width: 200px;
        max-height: 44px;
        overflow: hidden;
        padding: 4px 2px;
        box-sizing: border-box;
      }

      .user-role-chip {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        background: #e8f2f3;
        color: #3a6570;
        border: 1px solid #c5d8dc;
        border-radius: 12px;
        padding: 2px 8px;
        font-size: 10px;
        font-weight: 600;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .user-role-empty {
        color: #6c757d;
        font-size: 13px;
      }
    `,
  ],
})
export class UserRolesCellRendererComponent implements ICellRendererAngularComp {
  private readonly cdr = inject(ChangeDetectorRef);
  roles: string[] = [];

  agInit(params: ICellRendererParams<UserListRow>): void {
    this.setRoles(params);
    this.cdr.markForCheck();
  }

  refresh(params: ICellRendererParams<UserListRow>): boolean {
    this.setRoles(params);
    this.cdr.markForCheck();
    return true;
  }

  private setRoles(params: ICellRendererParams<UserListRow>): void {
    const fromRow = params.data?.userRoles ?? [];
    if (fromRow.length > 0) {
      this.roles = fromRow;
      return;
    }

    const fromValue = String(params.value ?? '').trim();
    if (fromValue && fromValue !== 'N/A') {
      this.roles = fromValue
        .split(',')
        .map((r) => r.trim())
        .filter((r) => r.length > 0);
      if (this.roles.length > 0) {
        return;
      }
    }

    const legacy = String(params.data?.userRole ?? '').trim();
    if (!legacy || legacy === 'N/A') {
      this.roles = [];
      return;
    }

    this.roles = legacy
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
  }
}
